// First-touch attribution capture. Stores UTM params, referrer, landing page,
// and ad click IDs in localStorage on first page load so we can attach them
// when a visitor eventually places an order.

const STORAGE_KEY = "mfi_attribution";

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  referrer_url?: string;
  landing_page?: string;
  referral_code?: string;
  first_seen_at?: string;
}

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

function truncate(v: string | null | undefined, max = 500): string | undefined {
  if (!v) return undefined;
  const trimmed = v.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, max);
}

/**
 * Capture attribution on first page load. First touch wins — if a record
 * already exists in localStorage, it's preserved.
 */
const LAST_TOUCH_KEY = "mfi_attribution_last";

/**
 * Capture attribution. First touch is preserved forever; a visit that arrives
 * with campaign parameters or a click id (utm_*, fbclid, gclid) is also stored
 * as the last touch, so a returning visitor who clicks a Meta ad is credited to
 * that ad even if their first visit was organic. Never throws, never blocks.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const refCode = truncate(params.get("ref"), 40);

    const touch: Attribution = { first_seen_at: new Date().toISOString() };
    const ref = truncate(document.referrer);
    if (ref) touch.referrer_url = ref;
    const lp = truncate(window.location.href);
    if (lp) touch.landing_page = lp;
    let hasCampaign = false;
    for (const key of TRACKED_PARAMS) {
      const v = truncate(params.get(key));
      if (v) {
        (touch as Record<string, string>)[key] = v;
        hasCampaign = true;
      }
    }
    if (hasCampaign) window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touch));

    const existingRaw = window.localStorage.getItem(STORAGE_KEY);
    if (existingRaw) {
      if (refCode) {
        try {
          const existing = JSON.parse(existingRaw) as Attribution;
          existing.referral_code = refCode.toUpperCase();
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
        } catch {
          // ignore
        }
      }
      return;
    }
    if (refCode) touch.referral_code = refCode.toUpperCase();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // localStorage disabled or quota exceeded — silently ignore.
  }
}

const readJson = (key: string): Attribution => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    return {};
  }
};

/** First-touch record only. */
export function getFirstTouchAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  return readJson(STORAGE_KEY);
}

/**
 * Effective attribution written onto leads/orders: the last campaign touch
 * (utm_* / click ids / its landing page) when one exists, otherwise first touch.
 * Referral code always comes from the first-touch record, which ?ref= updates.
 */
export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const first = readJson(STORAGE_KEY);
  const last = readJson(LAST_TOUCH_KEY);
  return mergeAttribution(first, last);
}

/** Pure merge, exported for tests. */
export function mergeAttribution(first: Attribution, last: Attribution): Attribution {
  const hasLast = TRACKED_PARAMS.some((k) => typeof last[k] === "string" && last[k]);
  if (!hasLast) return { ...first };
  const out: Attribution = { ...last };
  if (first.referral_code) out.referral_code = first.referral_code;
  if (first.first_seen_at) out.first_seen_at = first.first_seen_at;
  return out;
}

export const HEARD_ABOUT_US_OPTIONS = [
  "Google search",
  "Facebook or Instagram",
  "Friend or neighbor referral",
  "Saw our truck or yard sign",
  "Nextdoor",
  "Home show or event",
  "Other",
] as const;

export type HeardAboutUsOption = (typeof HEARD_ABOUT_US_OPTIONS)[number];

/**
 * Attribution as flat database columns, for lead/order tables that store
 * utm_* / click ids directly. Absent values are omitted rather than written as
 * empty strings, so "unattributed" stays honestly null in reporting.
 */
export function attributionColumns(): Record<string, string> {
  const a = getAttribution();
  const cols: Record<string, string> = {};
  const map: Array<[keyof Attribution, string]> = [
    ["utm_source", "utm_source"],
    ["utm_medium", "utm_medium"],
    ["utm_campaign", "utm_campaign"],
    ["utm_term", "utm_term"],
    ["utm_content", "utm_content"],
    ["gclid", "gclid"],
    ["fbclid", "fbclid"],
    ["referrer_url", "referrer_url"],
    ["landing_page", "landing_page"],
  ];
  for (const [key, col] of map) {
    const v = a[key];
    if (typeof v === "string" && v) cols[col] = v;
  }
  return cols;
}
